import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import API from '../../api/axios';
import { formatPrice, getOrderStatusColor } from '../../utils/helpers';
import toast from 'react-hot-toast';

const statusOptions = ['pending', 'confirmed', 'processing', 'shipped', 'delivered', 'cancelled'];

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchParams] = useSearchParams();
  const statusFilter = searchParams.get('status') || '';

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (statusFilter) params.set('status', statusFilter);
      const { data } = await API.get(`/orders?${params}`);
      setOrders(data.orders);
      setTotalPages(data.pages);
      setTotal(data.total);
    } catch {
      toast.error('Error loading orders');
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  const updateStatus = async (orderId, field, value) => {
    try {
      const updated = await API.put(`/orders/${orderId}`, { [field]: value });
      setOrders((prev) => prev.map((o) => o._id === orderId ? { ...o, ...updated.data } : o));
      if (selectedOrder?._id === orderId) setSelectedOrder({ ...selectedOrder, ...updated.data });
      toast.success('Order updated');
    } catch {
      toast.error('Error updating order');
    }
  };

  return (
    <div className="flex gap-6 h-full">
      {/* Orders list */}
      <div className={`${selectedOrder ? 'hidden lg:flex lg:flex-col w-96 flex-shrink-0' : 'flex-1 flex flex-col'}`}>
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Orders</h1>
            <p className="text-gray-500 text-sm">{total} total orders</p>
          </div>
        </div>

        <div className="card overflow-hidden flex-1 flex flex-col">
          <div className="overflow-x-auto flex-1">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Order</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Total</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase hidden lg:table-cell">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <tr key={i}><td colSpan={5} className="px-4 py-3"><div className="h-8 bg-gray-100 rounded animate-pulse" /></td></tr>
                  ))
                ) : orders.map((order) => (
                  <tr
                    key={order._id}
                    onClick={() => setSelectedOrder(order)}
                    className={`cursor-pointer hover:bg-gray-50 transition-colors ${selectedOrder?._id === order._id ? 'bg-primary-50' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <p className="font-medium text-sm text-primary-600">{order.orderNumber}</p>
                      <p className="text-xs text-gray-400">{order.items?.length} item(s)</p>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-sm text-gray-600">{order.shippingAddress?.name}</td>
                    <td className="px-4 py-3 font-semibold text-sm">{formatPrice(order.total)}</td>
                    <td className="px-4 py-3">
                      <span className={`badge ${getOrderStatusColor(order.orderStatus)} capitalize text-xs`}>{order.orderStatus}</span>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 hidden lg:table-cell">{new Date(order.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {orders.length === 0 && !loading && (
              <div className="text-center py-12 text-gray-400">No orders found</div>
            )}
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center gap-2 p-4 border-t border-gray-100">
              <button onClick={() => setPage(page - 1)} disabled={page === 1} className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-40">Prev</button>
              <span className="px-3 py-1 text-sm text-gray-600">{page}/{totalPages}</span>
              <button onClick={() => setPage(page + 1)} disabled={page === totalPages} className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-40">Next</button>
            </div>
          )}
        </div>
      </div>

      {/* Order detail */}
      {selectedOrder && (
        <div className="flex-1 card p-6 overflow-y-auto">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-xl font-bold text-gray-800">{selectedOrder.orderNumber}</h2>
              <p className="text-gray-500 text-sm">{new Date(selectedOrder.createdAt).toLocaleString()}</p>
            </div>
            <button onClick={() => setSelectedOrder(null)} className="lg:hidden text-gray-400 hover:text-gray-600">Close</button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-sm text-gray-700 mb-3">Shipping Address</h3>
              <p className="font-medium">{selectedOrder.shippingAddress?.name}</p>
              <p className="text-sm text-gray-600">{selectedOrder.shippingAddress?.phone}</p>
              <p className="text-sm text-gray-600">{selectedOrder.shippingAddress?.street}</p>
              <p className="text-sm text-gray-600">{selectedOrder.shippingAddress?.city}, {selectedOrder.shippingAddress?.state}</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <h3 className="font-semibold text-sm text-gray-700 mb-3">Payment</h3>
              <p className="text-sm capitalize"><span className="text-gray-500">Method:</span> {selectedOrder.paymentMethod === 'cod' ? 'Cash on Delivery' : 'Bank Transfer'}</p>
              <p className="text-sm capitalize"><span className="text-gray-500">Status:</span> {selectedOrder.paymentStatus}</p>
              {selectedOrder.trackingNumber && <p className="text-sm"><span className="text-gray-500">Tracking:</span> {selectedOrder.trackingNumber}</p>}
            </div>
          </div>

          {/* Items */}
          <div className="mb-6">
            <h3 className="font-semibold text-sm text-gray-700 mb-3">Order Items</h3>
            <div className="space-y-2">
              {selectedOrder.items?.map((item, i) => (
                <div key={i} className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-medium text-sm">{item.name}</p>
                    <p className="text-xs text-gray-500">Qty: {item.quantity} &times; {formatPrice(item.price)}</p>
                  </div>
                  <span className="font-bold text-sm">{formatPrice(item.price * item.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 border-t border-gray-100 pt-3 text-right">
              <p className="text-sm text-gray-600">Shipping: {formatPrice(selectedOrder.shippingCost || 0)}</p>
              <p className="text-lg font-bold text-primary-600 mt-1">Total: {formatPrice(selectedOrder.total)}</p>
            </div>
          </div>

          {/* Update status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Order Status</label>
              <select value={selectedOrder.orderStatus} onChange={(e) => updateStatus(selectedOrder._id, 'orderStatus', e.target.value)} className="input-field">
                {statusOptions.map((s) => <option key={s} value={s} className="capitalize">{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Tracking Number</label>
              <div className="flex gap-2">
                <input defaultValue={selectedOrder.trackingNumber} onBlur={(e) => { if (e.target.value !== selectedOrder.trackingNumber) updateStatus(selectedOrder._id, 'trackingNumber', e.target.value); }} className="input-field" placeholder="Enter tracking number" />
              </div>
            </div>
          </div>

          {selectedOrder.notes && (
            <div className="mt-4 bg-yellow-50 rounded-xl p-4">
              <p className="text-sm font-medium text-yellow-700 mb-1">Customer Notes:</p>
              <p className="text-sm text-yellow-600">{selectedOrder.notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
